import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  useOutputProcessingEventQuery,
  useLatestOutputProcessingGroupLatestEventQuery,
  OutputProcessingEventDetailsType,
  ProcessingEventReport,
  formatDate,
  Tooltip,
  getUserName,
  useSystemClassifierItemsBy,
  formatDateTimeShort,
  Padding,
  useQueryParams,
  OutputProcessingErrorExcelUrl,
} from '@atlas-frontend/sdk';
import { expand } from 'uri-template-lite';
import { useNumericParam } from '../../../common/useEntityIdParam';
import { NotFound } from '../../NotFound';
import { ErrorReportType } from '../types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './OutputDataProcessingErrorReport.translations.json';
import { OutputDataProcessingErrorReportButtonNav } from './OutputDataProcessingErrorReportButtonNav';
import { CalculationErrorsTable } from './CalculationErrorsTable';
import { ValidationErrorsTable } from './ValidationErrorsTable';
import { MissingSourceDataErrorsTable } from './MissingSourceDataErrorsTable';

const { addTranslations, useTranslation } = i18nNsFactory(
  'output-data-processing-error-report',
);
addTranslations(translations);

export const OutputDataProcessingErrorReport: FC = () => {
  const { type } = useParams<{ type: ErrorReportType }>();
  const id = useNumericParam('id');

  if (!type || !id) {
    return <NotFound />;
  }

  switch (type) {
    case 'processingEvent':
      return <ProcessingEventErrorReport id={id} />;
    case 'processingGroup':
      return <ProcessingGroupErrorReport id={id} />;
    case 'relatedOutputDataObject':
      return null;
  }
};

const ProcessingEventErrorReport: FC<{
  id: number;
}> = ({ id }) => {
  const [{ processingDataObjectId: processingDataObjectIdString }] =
    useQueryParams();
  const processingDataObjectId = processingDataObjectIdString
    ? Number(processingDataObjectIdString)
    : undefined;
  const { data, isLoading } = useOutputProcessingEventQuery(
    id,
    processingDataObjectId,
    {
      throwOnError: true,
    },
  );
  const title =
    data?.calculationProcessingEvent?.outputDataObjectDescription ??
    data?.validationProcessingEvent?.outputDataObjectDescription ??
    data?.missingSourceDataProcessingEvent?.outputDataObjectDescription ??
    '';
  return (
    <OutputDataProcessingErrorReportTemplate
      type="processingEvent"
      id={id}
      processingDataObjectId={processingDataObjectId}
      title={title}
      data={data}
      isLoading={isLoading}
    />
  );
};

const ProcessingGroupErrorReport: FC<{ id: number }> = ({ id }) => {
  const { data, isLoading } = useLatestOutputProcessingGroupLatestEventQuery(
    id,
    { throwOnError: true },
  );
  return (
    <OutputDataProcessingErrorReportTemplate
      type="processingGroup"
      id={id}
      title={data?.dataObjectName || ''}
      data={data?.processingEventDetails}
      isLoading={isLoading}
    />
  );
};

const OutputDataProcessingErrorReportTemplate: FC<{
  type: ErrorReportType;
  id: number;
  processingDataObjectId?: number;
  title: string;
  isLoading: boolean;
  data: OutputProcessingEventDetailsType | undefined;
}> = ({ type, id, processingDataObjectId, title, data, isLoading }) => {
  const { t } = useTranslation();
  const [{ errorSegment, descriptionId: descriptionIdString }] =
    useQueryParams();
  const descriptionId = descriptionIdString
    ? Number(descriptionIdString)
    : undefined;

  const systemClassifierItemsById = useSystemClassifierItemsBy([
    'PROCESSING_METHOD',
    'PROCESSING_ERROR_LEVEL',
  ]);

  const meta = useMemo(() => {
    if (!data) {
      return [];
    }

    let processingEvent;
    if (errorSegment === 'calculationErrors') {
      processingEvent = data.calculationProcessingEvent;
    } else if (errorSegment === 'validationErrors') {
      processingEvent = data.validationProcessingEvent;
    } else if (errorSegment === 'missingSourceData') {
      processingEvent = data.missingSourceDataProcessingEvent;
    }

    const counts = {
      calculationErrors: data.calculationErrors.length,
      validationErrors: data.validationErrors.length,
      missingSourceData: data.missingSourceDataErrors.length,
    };

    const valueDate =
      data.calculationProcessingEvent?.valueDate ||
      data.validationProcessingEvent?.valueDate ||
      data.missingSourceDataProcessingEvent?.valueDate;

    const meta = [
      {
        label: t('field.valueDate'),
        value: formatDate(valueDate),
      },
      {
        value: (
          <Padding vertical={24}>
            <OutputDataProcessingErrorReportButtonNav
              type={type}
              id={id}
              processingDataObjectId={processingDataObjectId}
              descriptionId={descriptionId}
              counts={counts}
            />
          </Padding>
        ),
        style: { columnSpan: 'all' as const },
      },
    ] as { label?: string; value: React.ReactNode }[];

    if (!processingEvent) {
      return meta;
    }

    const {
      calculationStartTime,
      calculationEndTime,
      validationStartTime,
      validationEndTime,
      createdBy,
      processingMethodId,
      errorLevelId,
    } = processingEvent;

    if (errorSegment === 'validationErrors') {
      meta.push(
        {
          label: t('field.validationStartTime'),
          value: formatDateTimeShort(validationStartTime),
        },
        {
          label: t('field.validationEndTime'),
          value: formatDateTimeShort(validationEndTime),
        },
      );
    } else {
      meta.push(
        {
          label: t('field.calculationStartTime'),
          value: formatDateTimeShort(calculationStartTime),
        },
        {
          label: t('field.calculationEndTime'),
          value: formatDateTimeShort(calculationEndTime),
        },
      );
    }

    meta.push(
      {
        label: t('field.createdBy'),
        value: (
          <Tooltip title={getUserName(createdBy)}>{createdBy.username}</Tooltip>
        ),
      },
      {
        label: t('field.processingMethod'),
        value:
          systemClassifierItemsById.PROCESSING_METHOD.get(processingMethodId)
            ?.name,
      },
      {
        label: t('field.errorLevel'),
        value: systemClassifierItemsById.PROCESSING_ERROR_LEVEL.get(
          errorLevelId || 0,
        )?.name,
      },
    );

    return meta;
  }, [
    data,
    errorSegment,
    t,
    type,
    id,
    processingDataObjectId,
    descriptionId,
    systemClassifierItemsById.PROCESSING_METHOD,
    systemClassifierItemsById.PROCESSING_ERROR_LEVEL,
  ]);

  return (
    <ProcessingEventReport
      title={title}
      downloadUrl={expand(OutputProcessingErrorExcelUrl, {
        id,
        type,
        processingDataObjectId,
      })}
      meta={meta}
      isLoading={isLoading}
    >
      {errorSegment === 'calculationErrors' && (
        <CalculationErrorsTable
          data={data?.calculationErrors ?? []}
          scrollContainerSelector="#processing-error-report-main-content"
        />
      )}
      {errorSegment === 'validationErrors' && (
        <ValidationErrorsTable
          validationErrors={data?.validationErrors ?? []}
          scrollContainerSelector="#processing-error-report-main-content"
        />
      )}
      {errorSegment === 'missingSourceData' && (
        <MissingSourceDataErrorsTable
          data={data?.missingSourceDataErrors ?? []}
          scrollContainerSelector="#processing-error-report-main-content"
        />
      )}
    </ProcessingEventReport>
  );
};
